<script>
export default {
  name: 'CommonTable',
  inheritAttrs: false,
  props: {
    // 列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 表格数据
    data: {
      type: Array,
      default: () => []
    },
    // 加载状态
    loading: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: true
    },
    stripe: {
      type: Boolean,
      default: false
    },
    // 是否自动追加多选列
    selectable: {
      type: Boolean,
      default: false
    },
    // 是否自动追加序号列
    showIndex: {
      type: Boolean,
      default: false
    },
    // 行 key
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    // 表格高度、最大高度
    height: {
      type: [String, Number],
      default: null
    },
    maxHeight: {
      type: [String, Number],
      default: null
    },
    // 分页配置
    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        pageSizes: [10, 20, 50, 100]
      })
    },
    showPagination: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'row-click',
    'selection-change',
    'sort-change',
    'current-change',
    'page-change',
    'size-change',
    'action',
    'update:pagination'
  ],
  computed: {
    tableColumns() {
      const cols = [...this.columns]
      if (this.showIndex && !cols.some(col => col.type === 'index')) {
        cols.unshift({ type: 'index', width: 60, align: 'center' })
      }
      if (this.selectable && !cols.some(col => col.type === 'selection')) {
        cols.unshift({ type: 'selection', width: 55, align: 'center' })
      }
      return cols
    },
    pageOffset() {
      if (!this.showPagination || !this.pagination) return 0
      const current = Number(this.pagination.page || this.pagination.currentPage) || 1
      const size = Number(this.pagination.pageSize) || 10
      return Math.max(current - 1, 0) * size
    }
  },
  methods: {
    renderCell(col, scope) {
      if (typeof col.formatter === 'function') {
        return col.formatter(scope.row, scope.column, scope.row[col.prop], scope.$index)
      }
      return scope.row[col.prop] || '-'
    },
    defaultIndexMethod(index) {
      return this.pageOffset + index + 1
    },
    handleSelectionChange(selection) {
      this.$emit('selection-change', selection)
    },
    handleRowClick(row, column, event) {
      this.$emit('row-click', row, column, event)
    },
    handleSortChange(sort) {
      this.$emit('sort-change', sort)
    },
    handleSizeChange(size) {
      const next = { ...this.pagination, pageSize: size, page: 1 }
      this.$emit('update:pagination', next)
      this.$emit('size-change', size)
      this.$emit('page-change', { page: 1, pageSize: size })
    },
    handleCurrentChange(page) {
      const next = { ...this.pagination, page }
      this.$emit('update:pagination', next)
      this.$emit('current-change', page)
      this.$emit('page-change', { page: page, pageSize: this.pagination.pageSize })
    },
    handleActionClick(action, scope) {
      this.$emit('action', { action, ...scope })
    }
  }
}
</script>

<template>
  <div class="common-table">
    <slot name="toolbar" />
    <el-table
      v-bind="$attrs"
      v-on="$listeners"
      :data="data"
      :border="border"
      :stripe="stripe"
      :row-key="rowKey"
      :height="height"
      :max-height="maxHeight"
      :tooltip-effect="'dark'"
      :show-overflow-tooltip="false"
      v-loading="loading"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
      @sort-change="handleSortChange"
    >
      <slot name="prepend" />
      <template v-for="(col, index) in tableColumns" >
        <el-table-column
          :key="col.key || `sel-${index}`"
          v-if="col.type === 'selection'"
          type="selection"
          :width="col.width || 55"
          :label="col.label"
          :align="col.align || 'center'"
          :fixed="col.fixed"
          :reserve-selection="col.reserveSelection"
          :selectable="col.selectable"
        />
        <el-table-column
          :key="col.key || `idx-${index}`"
          v-else-if="col.type === 'index'"
          type="index"
          :width="col.width || 60"
          :label="col.label"
          :align="col.align || 'center'"
          :fixed="col.fixed"
          :index="col.index || defaultIndexMethod"
        />
        <el-table-column
          :key="col.key || `exp-${col.prop || col.slot || index}`"
          v-else-if="col.type === 'expand'"
          type="expand"
          :width="col.width"
          :label="col.label"
          :align="col.align || 'center'"
          :fixed="col.fixed"
        >
          <template slot-scope="scope">
            <slot
              v-if="col.slot && $scopedSlots[col.slot]"
              :name="col.slot"
              v-bind="scope"
            />
            <slot
              v-else
              name="expand"
              v-bind="scope"
            />
          </template>
        </el-table-column>
        <el-table-column
          :key="col.key || `op-${col.prop || index}`"
          v-else-if="col.type === 'operation'"
          :label="col.label || '操作'"
          :width="col.width"
          :fixed="col.fixed || 'right'"
          :align="col.align || 'center'"
        >
          <template slot-scope="scope">
            <slot
              v-if="$scopedSlots[`op-${col.prop || 'default'}`]"
              :name="`op-${col.prop || 'default'}`"
              v-bind="scope"
            />
            <template v-else>
              <el-button
                v-for="(btn, idx) in col.actions || []"
                :key="btn.key || btn.action || btn.text || idx"
                :type="btn.type || 'text'"
                size="mini"
                :icon="btn.icon"
                @click="handleActionClick(btn.action || btn, scope)"
              >
                {{ btn.text }}
              </el-button>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          v-else
          :key="col.key || `col-${col.prop || index}`"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align || 'center'"
          :fixed="col.fixed"
          :sortable="col.sortable"
          :show-overflow-tooltip="col.showOverflowTooltip === true"
        >
          <template slot-scope="scope">
            <slot
              v-if="$scopedSlots[`col-${col.prop}`]"
              :name="`col-${col.prop}`"
              v-bind="scope"
            />
            <span v-else class="common-table__cell">{{ renderCell(col, scope) }}</span>
          </template>
        </el-table-column>
      </template>
      <slot name="append" />
    </el-table>

    <div v-if="showPagination" class="common-table__pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="pagination.page"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :page-sizes="pagination.pageSizes || [10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.common-table {
  width: 100%;
 ::v-deep .el-table .cell{
    display: flex;
    align-items: center;
  }
  &__pagination {
    margin-top: 16px;
    text-align: right;
  }

  &__cell {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .el-table {
    table-layout: fixed;
  }

  ::v-deep .el-table__empty-text {
    color: #909399;
  }
}
</style>
